import styled from '@emotion/styled';
import type { StylesConfig } from 'src/components/DropdownWrapper/DropdownWrapper';
import { select, scrollbar } from 'src/assets/colors';
import { fontSize, fontWeight } from 'src/assets/fonts-config';
import { CheckIcon } from 'src/assets/new-icon/CheckIcon';
import { ChevronIconBold } from 'src/assets/new-icon/ChevronIconBold';

interface SelectDropdownWrapperPropsType {
    isFilled: boolean;
}

export const SelectWrapper = styled('div', { label: 'SelectWrapper' })`
    margin-bottom: 16px;
`;

export const SelectDropdownWrapper = styled('div', { label: 'SelectDropdownWrapper' })<SelectDropdownWrapperPropsType>`
    position: relative;
    width: 100%;
    height: 40px;

    /* Scrollbar styles for dropdown menu */
    div[class*='-MenuList'] {
        &::-webkit-scrollbar {
            width: 4px;
            background-color: ${scrollbar.bgColorSecondary};
        }

        &::-webkit-scrollbar-thumb {
            background-color: ${scrollbar.bgColor};
            border-radius: 4px;
        }

        &::-webkit-scrollbar-track {
            border-radius: 4px;
        }
    }
`;

export const Option = styled('div', { label: 'Option' })<{ isSelected?: boolean; isFocused?: boolean }>`
    display: flex;
    align-items: center;
    flex-flow: row nowrap;
    gap: 8px;
    position: relative;
    color: ${select.txtColor};
    background-color: ${({ isSelected, isFocused }): string =>
        isSelected === true
            ? select.option.bgColorSecondary
            : isFocused === true
              ? select.option.bgColorTertiary
              : select.bgColorTertiary};
    font-size: ${fontSize.small};
    cursor: pointer;
    padding: 10px 12px;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
        background-color: ${({ isSelected }): string =>
            isSelected === true ? select.option.bgColorSecondary : select.option.bgColorTertiary};
    }
`;

export const SelectedValue = styled('div', { label: 'SelectedValue' })`
    display: flex;
    align-items: center;
    flex-flow: row nowrap;
    gap: 8px;
    position: relative;
    font-size: ${fontSize.xRegular};
    font-weight: ${fontWeight.bold};
    color: ${select.txtColor};
`;

export const ChevronIconWrapper = styled(ChevronIconBold, { label: 'ChevronIconWrapper' })<SelectDropdownWrapperPropsType>`
    position: absolute;
    top: 10px;
    right: 7px;
    fill: ${({ isFilled }): string => (isFilled ? select.chevron.bgColorSecondary : select.chevron.bgColor)};
`;

export const CheckIconWrapper = styled(CheckIcon, { label: 'CheckIconWrapper' })`
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    fill: currentcolor;
`;

export const getSelectStyles = <T, IsMulti extends boolean>(
    isFilled: boolean
): StylesConfig<T, IsMulti> => ({
    control: (base, state) => ({
        ...base,
        height: '40px',
        minHeight: '40px',
        borderRadius: 0,
        cursor: 'pointer',
        fontSize: fontSize.small,
        fontWeight: fontWeight.regular,
        backgroundColor: select.bgColor,
        lineHeight: '12px',
        borderColor: state.isFocused
            ? select.borderColorTertiary
            : isFilled
              ? select.borderColorSecondary
              : select.borderColor,
        boxShadow: 'none',
        '&:hover': {
            borderColor: select.borderColorTertiary,
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0 12px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
    }),
    placeholder: (base, state) => ({
        ...base,
        color: state.isFocused ? select.txtColor : select.txtColorSecondary,
        fontSize: fontSize.xRegular,
        fontWeight: 700,
        position: 'absolute',
        top: '10px',
        margin: 0,
    }),
    singleValue: (base) => ({
        ...base,
        color: select.txtColor,
        fontSize: fontSize.xRegular,
        fontWeight: fontWeight.bold,
        position: 'absolute',
        top: '10px',
        margin: 0,
    }),
    dropdownIndicator: () => ({
        display: 'none',
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    menu: (base) => ({
        ...base,
        boxShadow: '0px 2px 24px rgba(0, 0, 0, 0.2)',
        backgroundColor: select.option.bgColor,
        width: '100%',
        border: 'none',
        borderRadius: 0,
        margin: 0,
    }),
    menuList: (base) => ({
        ...base,
        maxHeight: '198px',
        padding: 0,
        overflowY: 'auto',
        scrollbarColor: `${scrollbar.bgColor} ${scrollbar.bgColorSecondary}`,
        scrollbarWidth: 'thin' as const,
    }),
    option: (base) => ({
        ...base,
    }),
    noOptionsMessage: (base) => ({
        ...base,
        color: select.txtColor,
        backgroundColor: select.bgColorTertiary,
        fontSize: fontSize.small,
        padding: '10px 12px',
    }),
});
